import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IconChevronLeft, IconChevronRight, IconLock, IconLogin2, IconPencil, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { ConfirmModal } from '../../../../shared/ui/ConfirmModal';
import type { User } from '../../../../entities/user';
import { pb } from '../../../../shared/api/pocketbase';
import { getManagerColor, getManagerInitials } from '../../../../shared/lib/utils';
import { Button } from '../../../../shared/ui/Button';
import { Input } from '../../../../shared/ui/Input';
import { UserModal } from '../../../../features/admin-user-form';
import { ImpersonateModal } from '../../../../features/impersonate';
import { DeleteUserModal } from '../../../../features/delete-user';

const PER_PAGE = 20;

const roleLabel = (role: string) => {
  if (role?.toUpperCase() === 'ADMIN') return 'Администратор';
  if (role?.toUpperCase() === 'MANAGER') return 'Тимлид';
  return 'Менеджер';
}

const roleBadgeClass = (role: string) => {
  if (role?.toUpperCase() === 'ADMIN') return 'adm-role-badge admin';
  if (role?.toUpperCase() === 'MANAGER') return 'adm-role-badge manager';
  return 'adm-role-badge user';
}

const pluralUsers = (n: number) => {
  if (n === 1) return '1 пользователь';
  if (n < 5) return `${n} пользователя`;
  return `${n} пользователей`;
}

const buildPageNumbers = (current: number, total: number): (number | '...')[] => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '...')[] = [1];
  if (current > 3) pages.push('...');
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) {
    pages.push(p);
  }
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
}

const UsersSection: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState<{ mode: 'add' | 'edit'; user?: User } | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [deleteUser, setDeleteUser] = useState<User | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<User | null>(null);
  const [confirmBlock, setConfirmBlock] = useState<User | null>(null);
  const [impersonateUser, setImpersonateUser] = useState<User | null>(null);
  const [blockingId, setBlockingId] = useState<string | null>(null);

  const handleToggleBlock = useCallback((u: User) => {
    const newIsBlocked = !u.isBlocked;
    setBlockingId(u.id);
    pb.collection('users').update(u.id, { isBlocked: newIsBlocked })
      .then(() => setUsers(prev => prev.map(x => x.id === u.id ? { ...x, isBlocked: newIsBlocked } : x)))
      .finally(() => setBlockingId(null));
  }, []);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadUsers = useCallback((p: number, q: string) => {
    setLoading(true);
    const safe = q.trim().replace(/"/g, '');
    const safeLower = safe.toLowerCase();
    let filter: string | undefined;
    if (safe) {
      if (safe.includes('@')) {
        filter = `email ~ "${safeLower}"`;
      } else {
        const safeTitle = safeLower.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        const variants = [...new Set([safe, safeLower, safeTitle])];
        const namePart = variants.map(v => `name ~ "${v}"`).join(' || ');
        filter = `(${namePart}) || email ~ "${safeLower}"`;
      }
    }
    pb.collection('users')
      .getList<User>(p, PER_PAGE, { sort: 'name', fields: 'id,name,email,phone,role,created,isBlocked', filter })
      .then(result => {
        setUsers(result.items);
        setTotalPages(result.totalPages);
        setTotalItems(result.totalItems);
      })
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadUsers(1, ''); }, [loadUsers]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(1);
      loadUsers(1, value);
    }, 300);
  };

  const handlePageChange = (p: number) => {
    setPage(p);
    loadUsers(p, search);
  };

  const pageNumbers = buildPageNumbers(page, totalPages);

  return (
    <div className="adm-section">
      <div className="adm-section-header adm-section-header-row">
        <div>
          <h2 className="adm-section-title">Пользователи</h2>
          <p className="adm-section-sub">{loading ? 'Загрузка...' : pluralUsers(totalItems)}</p>
        </div>
        <div className="adm-ut-toolbar">
          <div className="adm-input-wrap" style={{ width: 240 }}>
            <span className="adm-input-icon"><IconSearch size={15} stroke={2} /></span>
            <Input
              className="adm-input"
              placeholder="Поиск..."
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
            />
          </div>
          <Button variant="adm-add" type="button" onClick={() => setModal({ mode: 'add' })}>
            <IconPlus size={15} stroke={2.5} />Добавить
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="adm-loading">Загрузка...</div>
      ) : (
        <>
          <div className="adm-ut-wrap">
            <table className="adm-ut">
              <thead>
                <tr>
                  <th>Пользователь</th>
                  <th>Роль</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const ini = getManagerInitials(u.name || u.email, u.email);
                  const col = getManagerColor(u.id || u.name || u.email);
                  return (
                    <tr key={u.id} className="adm-ut-row">
                      <td>
                        <div className="adm-ut-user">
                          <div className="user-av" style={{ background: col, width: 28, height: 28, fontSize: 11, flexShrink: 0 }}>
                            {ini}
                          </div>
                          <div className="adm-ut-info">
                            <span className="adm-ut-name">{u.name || u.email}</span>
                            {u.name && <span className="adm-ut-email">{u.email}</span>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                          <span className={roleBadgeClass(u.role)}>{roleLabel(u.role)}</span>
                          {u.isBlocked && <span className="adm-role-badge blocked">Заблокирован</span>}
                        </div>
                      </td>
                      <td className="adm-ut-actions-cell">
                        <div className="adm-ut-actions">
                          <Button variant="adm-impersonate" type="button" title="Войти как" onClick={() => setImpersonateUser(u)}>
                            <IconLogin2 size={13} stroke={2} />
                          </Button>
                          <Button variant="adm-edit" type="button" title="Редактировать" onClick={() => setModal({ mode: 'edit', user: u })}>
                            <IconPencil size={13} stroke={2} />
                          </Button>
                          <Button
                            variant="adm-delete"
                            type="button"
                            title={u.isBlocked ? 'Разблокировать' : 'Заблокировать'}
                            disabled={blockingId === u.id}
                            onClick={() => setConfirmBlock(u)}
                          >
                            <IconLock size={13} stroke={2} />
                          </Button>
                          <Button variant="adm-delete" type="button" title="Удалить" onClick={() => setConfirmDelete(u)}>
                            <IconTrash size={13} stroke={2} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {users.length === 0 && (
                  <tr><td colSpan={3} className="adm-empty">Пользователи не найдены</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="adm-pagination">
              <button className="adm-page-btn" disabled={page === 1} onClick={() => handlePageChange(page - 1)} type="button">
                <IconChevronLeft size={15} stroke={2} />
              </button>
              {pageNumbers.map((p, i) =>
                p === '...'
                  ? <span key={`ellipsis-${i}`} className="adm-page-ellipsis">…</span>
                  : <button
                      key={p}
                      className={`adm-page-btn${p === page ? ' active' : ''}`}
                      onClick={() => handlePageChange(p)}
                      type="button"
                    >{p}</button>
              )}
              <button className="adm-page-btn" disabled={page === totalPages} onClick={() => handlePageChange(page + 1)} type="button">
                <IconChevronRight size={15} stroke={2} />
              </button>
            </div>
          )}
        </>
      )}

      {impersonateUser && (
        <ImpersonateModal
          userId={impersonateUser.id}
          userName={impersonateUser.name || impersonateUser.email}
          onClose={() => setImpersonateUser(null)}
        />
      )}

      {modal && (
        <UserModal
          mode={modal.mode}
          initial={modal.user ?? null}
          onClose={() => setModal(null)}
          onSaved={updated => {
            if (updated) {
              setUsers(prev => prev.map(u => u.id === updated.id ? updated : u));
            } else {
              loadUsers(page, search);
            }
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Удалить пользователя?"
          content={<>Вы уверены, что хотите удалить <strong>{confirmDelete.name || confirmDelete.email}</strong>? Это действие необратимо.</>}
          onConfirm={() => { setDeleteUser(confirmDelete); setConfirmDelete(null); }}
          onClose={() => setConfirmDelete(null)}
        />
      )}

      {confirmBlock && (
        <ConfirmModal
          title={confirmBlock.isBlocked ? 'Разблокировать пользователя?' : 'Заблокировать пользователя?'}
          content={
            confirmBlock.isBlocked
              ? <>Пользователь <strong>{confirmBlock.name || confirmBlock.email}</strong> снова получит доступ к системе.</>
              : <>Пользователь <strong>{confirmBlock.name || confirmBlock.email}</strong> потеряет доступ к системе.</>
          }
          onConfirm={() => { handleToggleBlock(confirmBlock); setConfirmBlock(null); }}
          onClose={() => setConfirmBlock(null)}
        />
      )}

      {deleteUser && (
        <DeleteUserModal
          user={deleteUser}
          onClose={() => setDeleteUser(null)}
          onDeleted={() => {
            setDeleteUser(null);
            loadUsers(page, search);
          }}
        />
      )}
    </div>
  );
};

export { UsersSection };
